/*
 * Copyright (c) 2023. Collabro Limited
 */

import React, { useRef, useState } from "react";
import { PopoutScreenProps } from "../Options/Options";
import {
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Popover,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import { Backspace, Close, Delete } from "@mui/icons-material";
import SortToggle from "./SortToggle";
import { useMutation } from "@tanstack/react-query";
import { saveSort } from "../../api";
import { AxiosError } from "axios";
import CheckIcon from "@mui/icons-material/Check";
import { TransitionGroup } from "react-transition-group";

export interface SortProps {
  field: string;
  value: "asc" | "desc" | null;
}

const SortScreen = (props: PopoutScreenProps) => {
  const [existingSortValue, setExistingSortValue] = useState<SortProps[]>(
    props.initialData
  );

  const valueRef = useRef<any>(null);
  const handleClose = () => {
    props.onClose(null);
  };

  const saveSortToPreferences = useMutation({
    mutationFn: (sort: SortProps[]) => {
      return saveSort(sort);
    },
    onSuccess: () => {
      props.setInitialData(existingSortValue);
      props.getLatestReportData();
    },
    onError: (err: AxiosError) => props.handleError(err),
  });
  const handleSortChange = (field: string, value: "asc" | "desc") => {
    setExistingSortValue((prevState: SortProps[]) => {
      //Find existing
      const existingIndex = prevState.findIndex((sp) => sp.field === field);

      //If the sort options does not exist, add it
      if (existingIndex === -1) {
        const newSortProps = [...prevState, { field, value }];

        //Check if the new list exceeds the limit, if it does, remove the oldest one
        if (newSortProps.length > 2) {
          return newSortProps.slice(1);
        }
        return newSortProps;
      }

      if (value === null || prevState[existingIndex].value === value) {
        return prevState.filter((sp) => sp.field !== field);
      } else if (prevState[existingIndex].value !== value) {
        const updatedSortProps = [...prevState];
        updatedSortProps[existingIndex].value = value;
        return updatedSortProps;
      }

      //If the field exists wirth the same value, remove it
      return prevState.filter((sp) => sp.field !== field);
    });
    // if (valueRef.current) {
    //   clearTimeout(valueRef.current);
    // }
    //
    // valueRef.current = setTimeout(() => {
    //   //TODO: Turn back on later
    //   // saveSortToPreferences.mutate({ field, value });
    // }, 1000);
  };

  const handleSortSave = () => {
    saveSortToPreferences.mutate(existingSortValue);
    handleClose();
  };

  const handleClear = () => {
    setExistingSortValue([]);
    if (valueRef.current) {
      clearTimeout(valueRef.current);
    }
    valueRef.current = setTimeout(() => {
      saveSortToPreferences.mutate([]);
    }, 500);
  };

  const handleDelete = (field: string) => {
    setExistingSortValue((prevState: SortProps[]) => {
      return prevState.filter((sp) => sp.field !== field);
    });
  };

  return (
    <Popover
      open={Boolean(props.anchorEl)}
      anchorEl={props.anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <div
        style={{
          padding: 10,
          width: 460,
        }}
      >
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
        <Divider>
          <Typography variant="h6">Sort</Typography>
        </Divider>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <SortToggle
            name={"Start Date"}
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name={"End Date"}
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Forename"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Surname"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Vantage Id"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Job Title"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Employer"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Core Crew"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Gender"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Discipline"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Team"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
          <SortToggle
            name="Department"
            sortValue={existingSortValue}
            onChange={handleSortChange}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              padding: 5,
            }}
          >
            <Button
              onClick={handleSortSave}
              variant="contained"
              size="small"
              startIcon={<CheckIcon />}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<Backspace />}
              onClick={handleClear}
            >
              Clear
            </Button>
          </div>
          <div
            style={{
              display: "flex",
              padding: 5,
            }}
          >
            <TransitionGroup>
              {existingSortValue.map((sortValue, index) => (
                <Slide direction="left" key={index}>
                  <Chip
                    label={`${index + 1}.${
                      sortValue.field
                    } - ${sortValue.value!.toUpperCase()}`}
                    sx={{ mr: 1 }}
                    onClick={() =>
                      handleSortChange(
                        sortValue.field,
                        sortValue.value === "asc" ? "desc" : "asc"
                      )
                    }
                    onDelete={() => handleDelete(sortValue.field)}
                    deleteIcon={<Delete />}
                  />
                </Slide>
              ))}
            </TransitionGroup>
          </div>
        </div>
      </div>
    </Popover>
  );
};

export default SortScreen;
